import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Trash2, Plus, Store, ClipboardList, CalendarDays, Layers, Wrench, Ticket, Gift, Users, Tag, Sparkles, Handshake } from "lucide-react";
import api, { fileUrl, img, apiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ImageUploader } from "@/components/ImageUploader";

const field = "fk-border rounded-lg mt-1";
const tab = "rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-xs sm:text-sm";

function F({ label, children }) {
  return <div><label className="text-sm font-medium">{label}</label>{children}</div>;
}

export default function AdminDashboard() {
  const { L } = useLang();
  const [vendors, setVendors] = useState([]);
  const [apps, setApps] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [series, setSeries] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [signups, setSignups] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [artisans, setArtisans] = useState([]);
  const [partners, setPartners] = useState([]);

  const [editor, setEditor] = useState(null); // { type, data }

  const loadAll = () => {
    api.get("/vendors/all").then((r) => setVendors(r.data)).catch(() => {});
    api.get("/market-applications").then((r) => setApps(r.data)).catch(() => {});
    api.get("/markets/all").then((r) => setMarkets(r.data)).catch(() => {});
    api.get("/series").then((r) => setSeries(r.data)).catch(() => {});
    api.get("/workshops/all").then((r) => setWorkshops(r.data)).catch(() => {});
    api.get("/workshop-signups").then((r) => setSignups(r.data)).catch(() => {});
    api.get("/coupons").then((r) => setCoupons(r.data)).catch(() => {});
    api.get("/gifts").then((r) => setGifts(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/artisans/all").then((r) => setArtisans(r.data)).catch(() => {});
    api.get("/partners").then((r) => setPartners(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const approveVendor = async (id, ok) => {
    try { await api.post(`/admin/vendors/${id}/${ok ? "approve" : "reject"}`); toast.success(L("已更新", "Updated")); loadAll(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const del = async (path) => {
    if (!window.confirm(L("确定删除？", "Delete this?"))) return;
    try { await api.delete(path); toast.success(L("已删除", "Deleted")); loadAll(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveEditor = async (data) => {
    const { type } = editor;
    const map = { market: "/markets", series: "/series", workshop: "/workshops", coupon: "/coupons", gift: "/gifts" };
    const base = map[type];
    try {
      if (data.id) await api.put(`${base}/${data.id}`, data);
      else await api.post(base, data);
      toast.success(L("已保存", "Saved"));
      setEditor(null);
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const updateApp = async (id, status) => {
    try { await api.put(`/market-applications/${id}`, { status }); loadAll(); } catch (e) { toast.error(apiError(e)); }
  };

  const toggleMarketListed = async (m) => {
    try { await api.put(`/markets/${m.id}`, { is_listed: !m.is_listed }); loadAll(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="font-display text-4xl sm:text-5xl">管理后台 / Admin</h1>

      <Tabs defaultValue="vendors" className="mt-6">
        <TabsList className="fk-border rounded-2xl bg-white p-1 flex flex-wrap h-auto gap-1">
          <TabsTrigger value="vendors" className={tab} data-testid="atab-vendors"><Store size={14} className="mr-1" />摊主 {vendors.filter(v=>v.status==="pending").length ? `(${vendors.filter(v=>v.status==="pending").length})` : ""}</TabsTrigger>
          <TabsTrigger value="apps" className={tab} data-testid="atab-apps"><ClipboardList size={14} className="mr-1" />市集申请</TabsTrigger>
          <TabsTrigger value="markets" className={tab} data-testid="atab-markets"><CalendarDays size={14} className="mr-1" />市集</TabsTrigger>
          <TabsTrigger value="series" className={tab} data-testid="atab-series"><Layers size={14} className="mr-1" />系列</TabsTrigger>
          <TabsTrigger value="workshops" className={tab} data-testid="atab-workshops"><Wrench size={14} className="mr-1" />工作坊</TabsTrigger>
          <TabsTrigger value="signups" className={tab} data-testid="atab-signups"><Users size={14} className="mr-1" />报名</TabsTrigger>
          <TabsTrigger value="coupons" className={tab} data-testid="atab-coupons"><Ticket size={14} className="mr-1" />优惠券</TabsTrigger>
          <TabsTrigger value="gifts" className={tab} data-testid="atab-gifts"><Gift size={14} className="mr-1" />礼品</TabsTrigger>
          <TabsTrigger value="categories" className={tab} data-testid="atab-categories"><Tag size={14} className="mr-1" />类别</TabsTrigger>
          <TabsTrigger value="artisans" className={tab} data-testid="atab-artisans"><Sparkles size={14} className="mr-1" />手作达人 {artisans.filter(a=>a.status==="pending").length ? `(${artisans.filter(a=>a.status==="pending").length})` : ""}</TabsTrigger>
          <TabsTrigger value="partners" className={tab} data-testid="atab-partners"><Handshake size={14} className="mr-1" />合作单位</TabsTrigger>
        </TabsList>

        {/* Vendors */}
        <TabsContent value="vendors" className="mt-6 space-y-3">
          {vendors.map((v) => (
            <div key={v.id} className="bg-white fk-border rounded-xl p-4 flex items-center justify-between gap-4" data-testid={`admin-vendor-${v.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <img src={fileUrl(v.photos?.[0]) || "https://images.unsplash.com/photo-1777846800324-0cbb1124fb9a?w=200&q=80"} alt="" className="w-12 h-12 rounded-lg fk-border object-cover" />
                <div className="min-w-0">
                  <div className="font-semibold truncate">{v.name_zh} {v.name_en && `· ${v.name_en}`}</div>
                  <div className="text-xs text-black/50 truncate">{v.category} · {v.contact_email}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`font-mono-fk text-xs rounded px-2 py-1 ${v.status === "approved" ? "bg-[#00C853] text-white" : v.status === "rejected" ? "bg-[#FF5757] text-white" : "bg-[#FFD93D]"}`}>{v.status}</span>
                {v.status !== "approved" && <button onClick={() => approveVendor(v.id, true)} data-testid={`approve-${v.id}`} className="fk-border rounded-full p-2 bg-[#00E5FF] fk-lift" aria-label="approve"><Check size={16} /></button>}
                {v.status !== "rejected" && <button onClick={() => approveVendor(v.id, false)} data-testid={`reject-${v.id}`} className="fk-border rounded-full p-2 bg-white fk-lift" aria-label="reject"><X size={16} /></button>}
              </div>
            </div>
          ))}
          {vendors.length === 0 && <p className="text-black/50 font-mono-fk">暂无摊主</p>}
        </TabsContent>

        {/* Applications */}
        <TabsContent value="apps" className="mt-6 space-y-3">
          {apps.map((a) => (
            <div key={a.id} className="bg-white fk-border rounded-xl p-4">
              <div className="flex justify-between items-start gap-3">
                <div>
                  <div className="font-semibold">{a.vendor_name} <span className="text-black/50 font-normal text-sm">→ {a.market_title}</span></div>
                  <div className="text-sm text-black/60 mt-1">{a.contact_name} · {a.phone} · {a.email}</div>
                  {a.product_desc && <p className="text-sm text-black/60 mt-1">{a.product_desc}</p>}
                </div>
                <select value={a.status} onChange={(e) => updateApp(a.id, e.target.value)} data-testid={`app-status-${a.id}`} className="fk-border rounded-lg px-2 py-1 text-sm">
                  <option value="pending">pending</option>
                  <option value="approved">approved</option>
                  <option value="rejected">rejected</option>
                </select>
              </div>
            </div>
          ))}
          {apps.length === 0 && <p className="text-black/50 font-mono-fk">暂无申请</p>}
        </TabsContent>

        {/* Markets */}
        <TabsContent value="markets" className="mt-6 space-y-3">
          <button onClick={() => setEditor({ type: "market", data: { title_zh: "", title_en: "", description_zh: "", description_en: "", date: "", location_zh: "", location_en: "", cover_image: "", images: [], proposal_url: "", map_image: "", map_url: "", status: "upcoming", is_listed: true, apply_open: true, series: "" } })} data-testid="add-market" className="fk-border rounded-full px-4 py-2 text-sm font-semibold bg-[#FFD93D] fk-lift flex items-center gap-1"><Plus size={15} /> {L("新增市集", "Add market")}</button>
          {markets.map((m) => (
            <div key={m.id} className="bg-white fk-border rounded-xl p-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <img src={img(m.cover_image)} alt="" className="w-14 h-14 rounded-lg fk-border object-cover" />
                <div className="min-w-0"><div className="font-semibold truncate">{m.title_zh}</div><div className="text-xs text-black/50">{m.date} · {m.status}</div></div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <label className="flex items-center gap-1 text-xs"><Switch checked={m.is_listed} onCheckedChange={() => toggleMarketListed(m)} data-testid={`list-${m.id}`} />{L("列出", "List")}</label>
                <button onClick={() => setEditor({ type: "market", data: { ...m } })} className="fk-border rounded-full px-3 py-1.5 text-xs bg-white fk-lift">{L("编辑", "Edit")}</button>
                <button onClick={() => del(`/markets/${m.id}`)} className="fk-border rounded-full p-2 bg-white fk-lift" aria-label="del"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </TabsContent>

        {/* Series */}
        <TabsContent value="series" className="mt-6 space-y-3">
          <button onClick={() => setEditor({ type: "series", data: { title_zh: "", title_en: "", intro_zh: "", intro_en: "", cover_image: "", images: [], whatsapp: "", order: series.length + 1 } })} data-testid="add-series" className="fk-border rounded-full px-4 py-2 text-sm font-semibold bg-[#FFD93D] fk-lift flex items-center gap-1"><Plus size={15} /> {L("新增系列", "Add series")}</button>
          {series.map((s) => (
            <div key={s.id} className="bg-white fk-border rounded-xl p-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0"><img src={img(s.cover_image)} alt="" className="w-14 h-14 rounded-lg fk-border object-cover" /><div className="font-semibold truncate">{s.title_zh}</div></div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setEditor({ type: "series", data: { ...s } })} className="fk-border rounded-full px-3 py-1.5 text-xs bg-white fk-lift">{L("编辑", "Edit")}</button>
                <button onClick={() => del(`/series/${s.id}`)} className="fk-border rounded-full p-2 bg-white fk-lift" aria-label="del"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </TabsContent>

        {/* Workshops */}
        <TabsContent value="workshops" className="mt-6 space-y-3">
          <button onClick={() => setEditor({ type: "workshop", data: { title_zh: "", title_en: "", description_zh: "", description_en: "", date: "", time: "", location_zh: "", location_en: "", price: "", capacity: 20, image: "", is_active: true } })} data-testid="add-workshop" className="fk-border rounded-full px-4 py-2 text-sm font-semibold bg-[#FFD93D] fk-lift flex items-center gap-1"><Plus size={15} /> {L("新增工作坊", "Add workshop")}</button>
          {workshops.map((w) => (
            <div key={w.id} className="bg-white fk-border rounded-xl p-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0"><img src={img(w.image)} alt="" className="w-14 h-14 rounded-lg fk-border object-cover" /><div className="min-w-0"><div className="font-semibold truncate">{w.title_zh}</div><div className="text-xs text-black/50">{w.date} {w.time}</div></div></div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setEditor({ type: "workshop", data: { ...w } })} className="fk-border rounded-full px-3 py-1.5 text-xs bg-white fk-lift">{L("编辑", "Edit")}</button>
                <button onClick={() => del(`/workshops/${w.id}`)} className="fk-border rounded-full p-2 bg-white fk-lift" aria-label="del"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </TabsContent>

        {/* Signups */}
        <TabsContent value="signups" className="mt-6 space-y-2">
          {signups.map((s) => (
            <div key={s.id} className="bg-white fk-border rounded-xl p-3 flex justify-between text-sm">
              <span className="font-medium">{s.name} <span className="text-black/50 font-normal">· {s.workshop_title}</span></span>
              <span className="text-black/60 font-mono-fk text-xs">{s.phone} · {s.email}</span>
            </div>
          ))}
          {signups.length === 0 && <p className="text-black/50 font-mono-fk">暂无报名</p>}
        </TabsContent>

        {/* Coupons */}
        <TabsContent value="coupons" className="mt-6 space-y-3">
          <button onClick={() => setEditor({ type: "coupon", data: { title_zh: "", title_en: "", description_zh: "", description_en: "", code: "", is_active: true } })} data-testid="add-coupon" className="fk-border rounded-full px-4 py-2 text-sm font-semibold bg-[#FFD93D] fk-lift flex items-center gap-1"><Plus size={15} /> {L("新增优惠券", "Add coupon")}</button>
          {coupons.map((c) => (
            <div key={c.id} className="bg-white fk-border rounded-xl p-4 flex items-center justify-between">
              <div><div className="font-semibold">{c.title_zh}</div><span className="font-mono-fk text-xs">{c.code}</span></div>
              <button onClick={() => del(`/coupons/${c.id}`)} className="fk-border rounded-full p-2 bg-white fk-lift" aria-label="del"><Trash2 size={14} /></button>
            </div>
          ))}
        </TabsContent>

        {/* Gifts */}
        <TabsContent value="gifts" className="mt-6 space-y-3">
          <button onClick={() => setEditor({ type: "gift", data: { title_zh: "", title_en: "", description_zh: "", description_en: "", points_required: 100, image: "", is_active: true } })} data-testid="add-gift" className="fk-border rounded-full px-4 py-2 text-sm font-semibold bg-[#FFD93D] fk-lift flex items-center gap-1"><Plus size={15} /> {L("新增礼品", "Add gift")}</button>
          {gifts.map((g) => (
            <div key={g.id} className="bg-white fk-border rounded-xl p-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3"><img src={img(g.image)} alt="" className="w-12 h-12 rounded-lg fk-border object-cover" /><div><div className="font-semibold">{g.title_zh}</div><span className="font-mono-fk text-xs">{g.points_required} pts</span></div></div>
              <button onClick={() => del(`/gifts/${g.id}`)} className="fk-border rounded-full p-2 bg-white fk-lift" aria-label="del"><Trash2 size={14} /></button>
            </div>
          ))}
        </TabsContent>

        {/* Categories */}
        <TabsContent value="categories" className="mt-6">
          <CategoryManager categories={categories} reload={loadAll} L={L} />
        </TabsContent>

        {/* Artisans */}
        <TabsContent value="artisans" className="mt-6">
          <ArtisanManager artisans={artisans} categories={categories} reload={loadAll} L={L} />
        </TabsContent>

        {/* Partners */}
        <TabsContent value="partners" className="mt-6">
          <PartnerManager partners={partners} reload={loadAll} L={L} />
        </TabsContent>
      </Tabs>

      {editor && <EntityEditor editor={editor} vendors={vendors} onClose={() => setEditor(null)} onSave={saveEditor} L={L} />}
    </div>
  );
}

function EntityEditor({ editor, vendors = [], onClose, onSave, L }) {
  const [data, setData] = useState(editor.data);
  const set = (k, v) => setData({ ...data, [k]: v });
  const { type } = editor;
  const approvedVendors = vendors.filter((v) => v.status === "approved");
  const toggleVendor = (id) => {
    const cur = data.vendor_ids || [];
    set("vendor_ids", cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  const titles = { market: "市集 / Market", series: "系列 / Series", workshop: "工作坊 / Workshop", coupon: "优惠券 / Coupon", gift: "礼品 / Gift" };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="fk-border-thick rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">{titles[type]}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <F label={L("标题（中）", "Title (ZH)")}><Input value={data.title_zh || ""} onChange={(e) => set("title_zh", e.target.value)} data-testid="ed-title-zh" className={field} /></F>
            <F label={L("标题（英）", "Title (EN)")}><Input value={data.title_en || ""} onChange={(e) => set("title_en", e.target.value)} className={field} /></F>
          </div>

          {(type === "market" || type === "workshop") && (
            <>
              <F label={L("描述（中）", "Description (ZH)")}><Textarea rows={2} value={data.description_zh || ""} onChange={(e) => set("description_zh", e.target.value)} className={field} /></F>
              <F label={L("描述（英）", "Description (EN)")}><Textarea rows={2} value={data.description_en || ""} onChange={(e) => set("description_en", e.target.value)} className={field} /></F>
              <div className="grid grid-cols-2 gap-3">
                <F label={L("日期", "Date")}><Input placeholder="2026-08-15" value={data.date || ""} onChange={(e) => set("date", e.target.value)} data-testid="ed-date" className={field} /></F>
                {type === "workshop" && <F label={L("时间", "Time")}><Input placeholder="14:00" value={data.time || ""} onChange={(e) => set("time", e.target.value)} className={field} /></F>}
                {type === "market" && <F label={L("系列", "Series")}><Input value={data.series || ""} onChange={(e) => set("series", e.target.value)} className={field} /></F>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <F label={L("地点（中）", "Location (ZH)")}><Input value={data.location_zh || ""} onChange={(e) => set("location_zh", e.target.value)} className={field} /></F>
                <F label={L("地点（英）", "Location (EN)")}><Input value={data.location_en || ""} onChange={(e) => set("location_en", e.target.value)} className={field} /></F>
              </div>
            </>
          )}

          {type === "series" && (
            <>
              <F label={L("简介（中）", "Intro (ZH)")}><Textarea rows={2} value={data.intro_zh || ""} onChange={(e) => set("intro_zh", e.target.value)} className={field} /></F>
              <F label={L("简介（英）", "Intro (EN)")}><Textarea rows={2} value={data.intro_en || ""} onChange={(e) => set("intro_en", e.target.value)} className={field} /></F>
              <F label="WhatsApp"><Input placeholder="60123456789" value={data.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} className={field} /></F>
            </>
          )}

          {(type === "coupon" || type === "gift") && (
            <>
              <F label={L("描述（中）", "Description (ZH)")}><Textarea rows={2} value={data.description_zh || ""} onChange={(e) => set("description_zh", e.target.value)} className={field} /></F>
              <F label={L("描述（英）", "Description (EN)")}><Textarea rows={2} value={data.description_en || ""} onChange={(e) => set("description_en", e.target.value)} className={field} /></F>
            </>
          )}
          {type === "coupon" && <F label={L("优惠码", "Code")}><Input value={data.code || ""} onChange={(e) => set("code", e.target.value)} data-testid="ed-code" className={field} /></F>}
          {type === "gift" && <F label={L("所需积分", "Points required")}><Input type="number" value={data.points_required || 0} onChange={(e) => set("points_required", parseInt(e.target.value) || 0)} data-testid="ed-points" className={field} /></F>}
          {type === "workshop" && <F label={L("价格", "Price")}><Input placeholder="RM 88" value={data.price || ""} onChange={(e) => set("price", e.target.value)} className={field} /></F>}

          {/* Cover image */}
          {(type === "market" || type === "series" || type === "workshop" || type === "gift") && (
            <F label={L("封面图片", "Cover image")}><div className="mt-2"><ImageUploader value={data[type === "workshop" || type === "gift" ? "image" : "cover_image"] || ""} onChange={(v) => set(type === "workshop" || type === "gift" ? "image" : "cover_image", v)} multiple={false} label={L("上传", "Upload")} /></div></F>
          )}
          {/* Extra images / proposal */}
          {(type === "market" || type === "series") && (
            <F label={L("宣传图（可多张）", "Gallery images")}><div className="mt-2"><ImageUploader value={data.images || []} onChange={(v) => set("images", v)} /></div></F>
          )}
          {type === "market" && (
            <F label={L("计划书 PDF", "Proposal PDF")}><div className="mt-2"><ImageUploader value={data.proposal_url || ""} onChange={(v) => set("proposal_url", v)} accept=".pdf,application/pdf" multiple={false} label="PDF" /></div></F>
          )}
          {type === "market" && (
            <>
              <F label={L("摊位分布图 / 地图图片", "Booth map / map image")}><div className="mt-2"><ImageUploader value={data.map_image || ""} onChange={(v) => set("map_image", v)} multiple={false} label={L("上传地图", "Upload map")} /></div></F>
              <F label={L("Google 地图链接", "Google Maps link")}><Input placeholder="https://maps.google.com/..." value={data.map_url || ""} onChange={(e) => set("map_url", e.target.value)} data-testid="ed-map-url" className={field} /></F>
            </>
          )}
          {type === "market" && (
            <div data-testid="ed-vendor-select">
              <label className="text-sm font-medium">{L("参与摊主（选择列出）", "Participating vendors (select to list)")}</label>
              <div className="mt-2 max-h-44 overflow-y-auto fk-border rounded-lg p-3 space-y-1.5 bg-[#FDFBF7]">
                {approvedVendors.length === 0 && <p className="text-xs text-black/50">{L("暂无已批准摊主", "No approved vendors yet")}</p>}
                {approvedVendors.map((v) => (
                  <label key={v.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={(data.vendor_ids || []).includes(v.id)} onChange={() => toggleVendor(v.id)} data-testid={`ed-vendor-${v.id}`} className="w-4 h-4" />
                    <span className="font-medium">{v.name_zh}{v.name_en ? ` · ${v.name_en}` : ""}</span>
                    <span className="text-black/40 text-xs">({v.category})</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-black/40 mt-1">{L("已选 ", "Selected ")}{(data.vendor_ids || []).length}</p>
            </div>
          )}
          {type === "market" && (
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm"><Switch checked={data.apply_open ?? true} onCheckedChange={(v) => set("apply_open", v)} />{L("开放申请", "Apply open")}</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={data.is_listed ?? true} onCheckedChange={(v) => set("is_listed", v)} />{L("公开列出", "Listed")}</label>
              <select value={data.status || "upcoming"} onChange={(e) => set("status", e.target.value)} className="fk-border rounded-lg px-2 text-sm"><option value="upcoming">upcoming</option><option value="past">past</option></select>
            </div>
          )}

          <button onClick={() => onSave(data)} data-testid="ed-save" className="w-full fk-border rounded-full px-6 py-3 font-semibold bg-[#FFD93D] fk-lift">{L("保存", "Save")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function CategoryManager({ categories, reload, L }) {
  const [form, setForm] = useState({ name_zh: "", name_en: "" });

  const add = async () => {
    if (!form.name_zh.trim()) { toast.error(L("请输入类别名称", "Enter a category name")); return; }
    try {
      await api.post("/categories", { ...form, order: categories.length + 1 });
      toast.success(L("已新增", "Added"));
      setForm({ name_zh: "", name_en: "" });
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm(L("确定删除？", "Delete this?"))) return;
    try { await api.delete(`/categories/${id}`); reload(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white fk-border rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[140px]">
          <label className="text-sm font-medium">{L("类别（中）", "Name (ZH)")}</label>
          <Input value={form.name_zh} onChange={(e) => setForm({ ...form, name_zh: e.target.value })} data-testid="cat-name-zh" className={field} />
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="text-sm font-medium">{L("类别（英）", "Name (EN)")}</label>
          <Input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} data-testid="cat-name-en" className={field} />
        </div>
        <button onClick={add} data-testid="cat-add" className="fk-border rounded-full px-5 py-2.5 font-semibold bg-[#FFD93D] fk-lift flex items-center gap-1"><Plus size={15} /> {L("新增", "Add")}</button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {categories.map((c) => (
          <div key={c.id} className="bg-white fk-border rounded-xl p-4 flex items-center justify-between" data-testid={`cat-row-${c.id}`}>
            <div><span className="font-semibold">{c.name_zh}</span> <span className="text-black/50 text-sm">{c.name_en}</span></div>
            <button onClick={() => remove(c.id)} className="fk-border rounded-full p-2 bg-white fk-lift" aria-label="del"><Trash2 size={14} /></button>
          </div>
        ))}
        {categories.length === 0 && <p className="text-black/50 font-mono-fk">暂无类别</p>}
      </div>
    </div>
  );
}

function ArtisanManager({ artisans, categories, reload, L }) {
  const [edit, setEdit] = useState(null); // artisan data being edited/created

  const act = async (id, kind) => {
    try { await api.post(`/admin/artisans/${id}/${kind}`); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm(L("确定删除？", "Delete this?"))) return;
    try { await api.delete(`/artisans/${id}`); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const save = async (data) => {
    try {
      if (data.id) await api.put(`/artisans/${data.id}`, data);
      else await api.post("/artisans", data);
      toast.success(L("已保存", "Saved")); setEdit(null); reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-3">
      <button onClick={() => setEdit({ name_zh: "", name_en: "", category: "", bio_zh: "", bio_en: "", contact_phone: "", contact_email: "", social: { instagram: "", facebook: "", whatsapp: "" }, photos: [] })} data-testid="add-artisan" className="fk-border rounded-full px-4 py-2 text-sm font-semibold bg-[#FFD93D] fk-lift flex items-center gap-1"><Plus size={15} /> {L("新增手作达人", "Add artisan")}</button>
      {artisans.map((a) => (
        <div key={a.id} className="bg-white fk-border rounded-xl p-4 flex items-center justify-between gap-3" data-testid={`admin-artisan-${a.id}`}>
          <div className="flex items-center gap-3 min-w-0">
            <img src={img(a.photos?.[0])} alt="" className="w-12 h-12 rounded-lg fk-border object-cover" />
            <div className="min-w-0"><div className="font-semibold truncate">{a.name_zh} {a.name_en && `· ${a.name_en}`}</div><div className="text-xs text-black/50 truncate">{a.category} · {a.contact_email}</div></div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`font-mono-fk text-xs rounded px-2 py-1 ${a.status === "approved" ? "bg-[#00C853] text-white" : a.status === "rejected" ? "bg-[#FF5757] text-white" : "bg-[#FFD93D]"}`}>{a.status}</span>
            {a.status !== "approved" && <button onClick={() => act(a.id, "approve")} data-testid={`artisan-approve-${a.id}`} className="fk-border rounded-full p-2 bg-[#00E5FF] fk-lift" aria-label="approve"><Check size={16} /></button>}
            {a.status !== "rejected" && <button onClick={() => act(a.id, "reject")} className="fk-border rounded-full p-2 bg-white fk-lift" aria-label="reject"><X size={16} /></button>}
            <button onClick={() => setEdit({ ...a })} className="fk-border rounded-full px-3 py-1.5 text-xs bg-white fk-lift">{L("编辑", "Edit")}</button>
            <button onClick={() => del(a.id)} className="fk-border rounded-full p-2 bg-white fk-lift" aria-label="del"><Trash2 size={14} /></button>
          </div>
        </div>
      ))}
      {artisans.length === 0 && <p className="text-black/50 font-mono-fk">暂无手作达人</p>}
      {edit && <ArtisanEditor data={edit} categories={categories} onClose={() => setEdit(null)} onSave={save} L={L} />}
    </div>
  );
}

function ArtisanEditor({ data: initial, categories, onClose, onSave, L }) {
  const [data, setData] = useState(initial);
  const set = (k, v) => setData({ ...data, [k]: v });
  const setSocial = (k, v) => setData({ ...data, social: { ...(data.social || {}), [k]: v } });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="fk-border-thick rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">手作达人 / Artisan</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <F label={L("名称（中）", "Name (ZH)")}><Input value={data.name_zh || ""} onChange={(e) => set("name_zh", e.target.value)} data-testid="art-name-zh" className={field} /></F>
            <F label={L("名称（英）", "Name (EN)")}><Input value={data.name_en || ""} onChange={(e) => set("name_en", e.target.value)} className={field} /></F>
          </div>
          <F label={L("类别", "Category")}>
            <select value={data.category || ""} onChange={(e) => set("category", e.target.value)} data-testid="art-category" className={field + " w-full px-2 py-2"}>
              <option value="">--</option>
              {categories.map((c) => <option key={c.id} value={c.name_zh}>{L(c.name_zh, c.name_en)}</option>)}
            </select>
          </F>
          <F label={L("简介（中）", "Bio (ZH)")}><Textarea rows={2} value={data.bio_zh || ""} onChange={(e) => set("bio_zh", e.target.value)} className={field} /></F>
          <F label={L("简介（英）", "Bio (EN)")}><Textarea rows={2} value={data.bio_en || ""} onChange={(e) => set("bio_en", e.target.value)} className={field} /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label={L("电话", "Phone")}><Input value={data.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className={field} /></F>
            <F label={L("邮箱", "Email")}><Input value={data.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className={field} /></F>
            <F label="WhatsApp"><Input value={data.social?.whatsapp || ""} onChange={(e) => setSocial("whatsapp", e.target.value)} className={field} /></F>
            <F label="Instagram"><Input value={data.social?.instagram || ""} onChange={(e) => setSocial("instagram", e.target.value)} className={field} /></F>
          </div>
          <F label={L("照片（可多张）", "Photos")}><div className="mt-2"><ImageUploader value={data.photos || []} onChange={(v) => set("photos", v)} /></div></F>
          <button onClick={() => onSave(data)} data-testid="art-save" className="w-full fk-border rounded-full px-6 py-3 font-semibold bg-[#FFD93D] fk-lift">{L("保存", "Save")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PartnerManager({ partners, reload, L }) {
  const [form, setForm] = useState({ name: "", url: "", logo: "" });
  const set = (k, v) => setForm({ ...form, [k]: v });

  const add = async () => {
    if (!form.name.trim()) { toast.error(L("请输入名称", "Enter a name")); return; }
    try { await api.post("/partners", { ...form, order: partners.length + 1 }); toast.success(L("已新增", "Added")); setForm({ name: "", url: "", logo: "" }); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm(L("确定删除？", "Delete this?"))) return;
    try { await api.delete(`/partners/${id}`); reload(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white fk-border rounded-xl p-4 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <F label={L("单位名称", "Partner name")}><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="partner-name" className={field} /></F>
          <F label={L("网址（可选）", "Website (optional)")}><Input placeholder="https://" value={form.url} onChange={(e) => set("url", e.target.value)} data-testid="partner-url" className={field} /></F>
        </div>
        <F label={L("Logo / 图片", "Logo / image")}><div className="mt-2"><ImageUploader value={form.logo} onChange={(v) => set("logo", v)} multiple={false} label={L("上传", "Upload")} /></div></F>
        <button onClick={add} data-testid="partner-add" className="fk-border rounded-full px-5 py-2.5 font-semibold bg-[#FFD93D] fk-lift flex items-center gap-1"><Plus size={15} /> {L("新增", "Add")}</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {partners.map((p) => (
          <div key={p.id} className="bg-white fk-border rounded-xl overflow-hidden" data-testid={`partner-row-${p.id}`}>
            {p.logo && <img src={img(p.logo)} alt="" className="w-full h-24 object-cover border-b-2 border-black" />}
            <div className="p-3 flex items-center justify-between">
              <span className="font-semibold truncate">{p.name}</span>
              <button onClick={() => del(p.id)} className="fk-border rounded-full p-2 bg-white fk-lift" aria-label="del"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {partners.length === 0 && <p className="text-black/50 font-mono-fk">暂无合作单位</p>}
      </div>
    </div>
  );
}

